'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';

interface ProcedureStepsProps {
  steps: {
    title: string;
    description: string;
    image?: string;
    duration?: string;
  }[];
}

export default function ProcedureSteps({ steps }: ProcedureStepsProps) {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div className="space-y-6">
      <div className="flex space-x-2 overflow-x-auto pb-2">
        {steps.map((step, index) => (
          <Button
            key={index}
            variant={activeStep === index ? "default" : "outline"}
            className={`${activeStep === index
              ? "bg-blue-900 text-white"
              : "border-blue-900 text-blue-900"
              } whitespace-nowrap`}
            onClick={() => setActiveStep(index)}
          >
            Step {index + 1}
          </Button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-bold mb-2">
                    Step {activeStep + 1}: {steps[activeStep].title}
                  </h3>

                  {steps[activeStep].duration && (
                    <p className="text-blue-700 text-sm mb-3">
                      Duration: {steps[activeStep].duration}
                    </p>
                  )}

                  <p className="text-gray-700">{steps[activeStep].description}</p>
                </div>

                {steps[activeStep].image && (
                  <div className="relative h-64 w-full rounded-lg overflow-hidden">
                    <Image
                      src={steps[activeStep].image!}
                      alt={steps[activeStep].title}
                      fill={true}
                      style={{ objectFit: "cover" }}
                      className="rounded-lg"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between mt-4">
        <Button
          variant="outline"
          onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))}
          disabled={activeStep === 0}
        >
          Previous
        </Button>
        <Button
          onClick={() => setActiveStep((prev) => Math.min(steps.length - 1, prev + 1))}
          disabled={activeStep === steps.length - 1}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
